
import React, { useState } from 'react';
import { X, Check, Loader2, Building2, User, Mail, Phone, MapPin, ShieldCheck, ArrowRight } from 'lucide-react';

interface GymOnboardingProps {
  onClose: () => void;
}

const GymOnboarding: React.FC<GymOnboardingProps> = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [formData, setFormData] = useState({
    gymName: '',
    contactName: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    plan: 'Plan Piloto',
    confirmPlan: false,
    acceptTerms: false,
    acceptQuality: false,
    acceptValidation: false,
    hasAuthority: false
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    // Check if user has scrolled to within 20px of the bottom
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  };

  const handleNextStep = async () => {
    // Check if all text fields are filled
    if (!formData.gymName || !formData.contactName || !formData.email || !formData.phone || !formData.city || !formData.address) {
      alert('Por favor, completa todos los campos del formulario.');
      return;
    }

    setCheckingEmail(true);
    setEmailError(null);

    try {
      const response = await fetch('/api/check-gym-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: 'Error desconocido' };
        }
        throw new Error(errorData.details || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Expected JSON but got:", text);
        throw new TypeError("Oops, we haven't got JSON!");
      }

      const data = await response.json();

      if (data.exists) {
        setEmailError(data.message);
      } else {
        setStep(2);
      }
    } catch (error: any) {
      console.error('Error checking email:', error);
      alert(error.message || 'Error al verificar el email. Inténtalo de nuevo.');
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if all text fields are filled
    if (!formData.gymName || !formData.contactName || !formData.email || !formData.phone || !formData.city || !formData.address) {
      alert('Por favor, completa todos los campos del formulario.');
      return;
    }

    if (!formData.acceptTerms || !formData.acceptQuality || !formData.acceptValidation || !formData.hasAuthority || !formData.confirmPlan) {
      alert('Debes aceptar todos los términos, confirmar el plan y declarar tu autoridad para continuar.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/gym-onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json();
        alert('Error: ' + (data.details || data.error || 'No se pudo enviar la solicitud.'));
      }
    } catch (error) {
      alert('Error de conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-dark-bg/95 backdrop-blur-xl animate-in fade-in duration-300">
        <div className="glass-card max-w-md w-full p-10 text-center border-brand/20">
          <div className="w-20 h-20 bg-brand rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_30px_rgba(255,255,255,0.2)]">
            <Check size={40} className="text-black" />
          </div>
          <h2 className="text-3xl font-display uppercase tracking-tighter text-white mb-4">¡Solicitud Enviada!</h2>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            Hemos enviado una copia de los términos a tu correo. Nuestro equipo se pondrá en contacto contigo pronto para iniciar el onboarding.
          </p>
          <button onClick={onClose} className="brutal-btn w-full">Cerrar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-dark-bg/95 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <div className="glass-card max-w-2xl w-full my-8 relative border-white/5">
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 text-white/20 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        <div className="p-8 sm:p-12">
          <div className="mb-10">
            <h2 className="text-3xl sm:text-4xl font-display uppercase tracking-tighter text-white mb-2">¿Quieres formar parte de la hermandad?</h2>
            <p className="text-brand text-[10px] uppercase tracking-[0.3em] font-bold">Elite Gym Onboarding</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {step === 1 ? (
              <div className="space-y-6 animate-in slide-in-from-right-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Nombre del Gimnasio</label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                      <input 
                        required
                        type="text" 
                        className="luxury-input w-full pl-12"
                        value={formData.gymName}
                        onChange={e => setFormData({...formData, gymName: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Persona de Contacto</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                      <input 
                        required
                        type="text" 
                        className="luxury-input w-full pl-12"
                        value={formData.contactName}
                        onChange={e => setFormData({...formData, contactName: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Email Corporativo</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                      <input 
                        required
                        type="email" 
                        className="luxury-input w-full pl-12"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Teléfono</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                      <input 
                        required
                        type="tel" 
                        className="luxury-input w-full pl-12"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Ciudad</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                      <input 
                        required
                        type="text" 
                        className="luxury-input w-full pl-12"
                        value={formData.city}
                        onChange={e => setFormData({...formData, city: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Plan de Suscripción</label>
                    <div className="relative">
                      <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-brand" size={18} />
                      <select 
                        className="luxury-input w-full pl-12 appearance-none cursor-pointer"
                        value={formData.plan}
                        onChange={e => setFormData({...formData, plan: e.target.value})}
                      >
                        <option value="Plan Piloto">Plan Piloto (Lanzamiento)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Dirección Exacta</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                    <input 
                      required
                      type="text" 
                      className="luxury-input w-full pl-12"
                      value={formData.address}
                      onChange={e => setFormData({...formData, address: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <button 
                    type="button"
                    onClick={handleNextStep}
                    disabled={checkingEmail}
                    className="brutal-btn w-full flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {checkingEmail ? (
                      <>Verificando... <Loader2 className="animate-spin" size={18} /></>
                    ) : (
                      <>Siguiente Paso <ArrowRight size={18} /></>
                    )}
                  </button>
                  
                  {emailError && (
                    <p className="text-red-500 text-xs font-bold uppercase tracking-widest text-center animate-in fade-in slide-in-from-top-2">
                      {emailError}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-8 animate-in slide-in-from-right-4">
                <div className="bg-white/5 rounded-2xl p-6 border border-white/5 space-y-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-display text-sm uppercase tracking-widest text-brand flex items-center gap-2">
                      <ShieldCheck size={16} /> Compromiso de la Hermandad
                    </h4>
                    <a 
                      href="https://hshqshiznfwasvgtcjaj.supabase.co/storage/v1/object/sign/Terminos%20y%20Condiciones/T&C%20Gym%20Bros%20v2.pdf?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9iYzQ1ODVmNy01ZWVlLTQ0MzctYjk3Mi1lNGYyYzQ0ZjM4ZmUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJUZXJtaW5vcyB5IENvbmRpY2lvbmVzL1QmQyBHeW0gQnJvcyB2Mi5wZGYiLCJpYXQiOjE3NzI3NjI2ODgsImV4cCI6NDg5NDgyNjY4OH0.EU7YTKaxYg9BKTzUXOnTwRZ4yFF4sHm1dsSL6l9kC-E" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[10px] text-white/40 hover:text-brand transition-colors flex items-center gap-1 border-b border-white/10 pb-0.5"
                    >
                      Descargar PDF Original
                    </a>
                  </div>
                  
                  <div 
                    onScroll={handleScroll}
                    className="max-h-64 overflow-y-auto pr-4 space-y-4 text-[11px] text-white/60 leading-relaxed font-sans border-b border-white/5 pb-6 mb-6 custom-scrollbar"
                  >
                    <p className="text-white font-bold uppercase tracking-widest mb-4 text-center border-b border-white/10 pb-4">TÉRMINOS Y CONDICIONES DE USO PARA GIMNASIOS ASOCIADOS</p>
                    
                    <p>Los presentes Términos y Condiciones (en adelante, los "T&C") regulan la relación comercial y el uso de la plataforma tecnológica provista por GYMBROTHERHOOD SpA (en adelante, "Gym Bros" o la "Plataforma") por parte de los gimnasios y centros de salud asociados (en adelante, el “Postulante”, "Cliente" o el "Gimnasio").</p>
                    
                    <p className="bg-white/5 p-4 rounded-lg border border-white/10 text-white italic">
                      AL HACER CLIC EN "ACEPTO" O MARCAR LA CASILLA DE ACEPTACIÓN, EL POSTULANTE DECLARA HABER LEÍDO, COMPRENDIDO Y ACEPTADO ÍNTEGRAMENTE LOS T&C, HABIENDO TENIDO LA OPORTUNIDAD DE ALMACENARLOS E IMPRIMIRLOS. NO OBSTANTE, LA ACEPTACIÓN DE ESTOS T&C CONSTITUYE ÚNICAMENTE UNA SOLICITUD DE ADHESIÓN A LA RED DE GIMNASIOS ASOCIADOS. LA RELACIÓN CONTRACTUAL SOLO SE PERFECCIONARÁ UNA VEZ QUE GYM BROS COMUNIQUE FORMALMENTE LA APROBACIÓN DE LA POSTULACIÓN, EN LOS TÉRMINOS ESTABLECIDOS EN LA CLÁUSULA 2 SIGUIENTE.
                    </p>

                    <p>LA PERSONA NATURAL QUE ACEPTA ESTOS T&C DECLARA BAJO JURAMENTO SER EL REPRESENTANTE LEGAL DEL POSTULANTE O CONTAR CON PODERES SUFICIENTES PARA OBLIGARLO COMERCIALMENTE.</p>

                    <p>ASIMISMO, AL HACER CLIC EN "ACEPTO" O MARCAR LA CASILLA DE ACEPTACIÓN, EL POSTULANTE CONFIRMA QUE EL CORREO ELECTRÓNICO INFORMADO POR ESTE SERÁ LA ÚNICA VÍA OFICIAL DE COMUNICACIÓN CON GYM BROS, SIENDO DE RESPONSABILIDAD EXCLUSIVA DEL POSTULANTE MANTENER SU CASILLA ELECTRÓNICA HABILITADA, CON CAPACIDAD SUFICIENTE Y OPERATIVA PARA RECIBIR LAS NOTIFICACIONES ELECTRÓNICAS QUE SE LE ENVÍEN, ASÍ COMO DE INFORMAR DEBIDAMENTE Y POR ESCRITO A GYM BROS CUALQUIER CAMBIO EN SU DIRECCIÓN DE CONTACTO.</p>

                    <p><strong>1. OBJETO Y SERVICIOS</strong></p>
                    <p>Gym Bros es titular y administrador de un sistema de registro y conexión de gimnasios y centros de salud. El objeto de estos T&C es establecer las condiciones bajo las cuales Gym Bros podrá otorgar al Cliente una licencia de uso y acceso a la Plataforma, permitiendo a este último ofrecer a sus propios usuarios finales el beneficio de acceder a la red de gimnasios asociados, y obligándose recíprocamente el Cliente a recibir en sus instalaciones a los usuarios de otros gimnasios de la red.</p>
                    <p>Gym Bros deja expresa constancia de que su relación contractual es exclusivamente con el Cliente. Gym Bros actúa como un mero intermediario tecnológico. En ningún caso se entenderá que existe vínculo contractual, societario, laboral o de consumo directo entre Gym Bros y los usuarios finales del Cliente, ni que Gym Bros presta servicios deportivos o de salud.</p>

                    <p><strong>2. POSTULACIÓN, EVALUACIÓN Y PERFECCIONAMIENTO DEL CONTRATO</strong></p>
                    <p>La aceptación de estos T&C por parte del Postulante constituye una solicitud de adhesión a la red de gimnasios asociados de Gym Bros, y no perfecciona por sí sola la relación contractual. El proceso de ingreso a la red se regirá por las siguientes etapas sucesivas:</p>
                    <ul className="list-decimal pl-5 space-y-2">
                      <li><strong>Postulación.</strong> El Postulante completará el formulario de registro en la Plataforma, proporcionando toda la información requerida (incluyendo, sin limitación, datos de identificación, patente municipal vigente, fotografías de las instalaciones, horarios de funcionamiento y capacidad), aceptará estos T&C y seleccionará el Plan de su preferencia.</li>
                      <li><strong>Evaluación.</strong> Gym Bros evaluará la postulación considerando, entre otros, los siguientes criterios objetivos: (a) cumplimiento de la normativa sanitaria, municipal y de seguridad aplicable al giro del Postulante; (b) estándares mínimos de infraestructura, equipamiento e higiene; (c) ubicación geográfica y compatibilidad con la red existente; y (d) disponibilidad horaria mínima según el Plan seleccionado.</li>
                      <li><strong>Resolución.</strong> Gym Bros comunicará al Postulante, al correo electrónico registrado, la aprobación o el rechazo de su postulación dentro de un plazo de quince (15) días hábiles contados desde la recepción de toda la documentación requerida.</li>
                      <li><strong>Perfeccionamiento.</strong> La relación contractual regulada por estos T&C se entenderá perfeccionada únicamente a partir de la fecha en que Gym Bros comunique formalmente la aprobación de la postulación mediante correo electrónico dirigido al Postulante (en adelante, la "Fecha de Activación").</li>
                    </ul>
                    <p>El rechazo de una postulación no generará derecho a indemnización alguna a favor del Postulante.</p>

                    <p><strong>3. PLANES, PRECIO Y ORDEN DE SERVICIO</strong></p>
                    <p>Las condiciones comerciales específicas, incluyendo la tarifa mensual a pagar por el Cliente, los meses de gracia (si aplican), y los mínimos de disponibilidad de horas y días exigidos, dependerán del plan seleccionado por el Cliente al momento de su postulación y confirmado en la Fecha de Activación (el "Plan").</p>
                    <p>Los detalles exactos del Plan constarán en el resumen de contratación o "Orden de Servicio" electrónica que se generará junto con la comunicación de aprobación. Dicha Orden de Servicio forma parte integrante e inseparable de estos T&C.</p>
                    <p>Al momento de la Fecha de Activación, el Cliente deberá pagar, como condición para el acceso a la Plataforma: (a) una matrícula única de incorporación, equivalente a una mensualidad del Plan seleccionado; y (b) la primera mensualidad proporcional.</p>
                    <p>A partir del mes calendario siguiente a la Fecha de Activación, las mensualidades se pagarán por adelantado, dentro de los primeros cinco (5) días hábiles de cada mes.</p>

                    <p><strong>4. OBLIGACIONES DEL CLIENTE</strong></p>
                    <p>Durante la vigencia de la relación comercial, el Cliente se obliga a:</p>
                    <ul className="list-decimal pl-5 space-y-2">
                      <li>Pagar íntegra y oportunamente la tarifa mensual acordada según el Plan seleccionado.</li>
                      <li>Permitir el ingreso y uso de sus instalaciones a los usuarios finales registrados en la Plataforma que provengan de otros gimnasios asociados, respetando los cupos y horarios mínimos establecidos en su Plan.</li>
                      <li>Mantener permanentemente actualizada su página de perfil dentro de la Plataforma, incluyendo horarios de funcionamiento, cupos disponibles, clases y fotografías.</li>
                      <li>Prestar sus servicios a los usuarios de la red en las mismas condiciones de calidad, seguridad e higiene que ofrece a sus propios clientes directos.</li>
                      <li>Concurrir al programa de capacitación de uso de la Plataforma impartido por Gym Bros.</li>
                      <li>Informar oportunamente la existencia de errores o mal funcionamientos de la Plataforma.</li>
                    </ul>

                    <p><strong>5. OBLIGACIONES DE GYM BROS</strong></p>
                    <p>Gym Bros se obliga a:</p>
                    <ul className="list-decimal pl-5 space-y-2">
                      <li>Otorgar acceso al Cliente a la Plataforma mientras éste se encuentre en cabal cumplimiento de sus obligaciones.</li>
                      <li>Prestar asesoría sobre el correcto uso de la Plataforma y realizar los ajustes necesarios para su funcionamiento.</li>
                      <li>Realizar la capacitación inicial al Cliente para el correcto uso de la Plataforma.</li>
                    </ul>

                    <p><strong>6. CONDICIONES DE USO PARA USUARIOS FINALES</strong></p>
                    <p>Los usuarios finales registrados en la Plataforma por cualquier gimnasio asociado tendrán derecho a acceder a otros gimnasios de la red bajo las siguientes reglas copulativas:</p>
                    <ul className="list-decimal pl-5 space-y-2">
                      <li>Podrán asistir a otros gimnasios asociados hasta un máximo de dos (2) veces por semana.</li>
                      <li>Las visitas semanales no son acumulables para las semanas siguientes.</li>
                      <li>El acceso estará sujeto a la disponibilidad de cupos y horarios publicados por cada gimnasio receptor.</li>
                    </ul>

                    <p><strong>7. PROTECCIÓN DE DATOS PERSONALES</strong></p>
                    <p>Ambas partes se obligan a dar estricto cumplimiento a la Ley N°19.628 sobre Protección de la Vida Privada y sus modificaciones. El Cliente, como responsable de los datos de sus propios usuarios, garantiza contar con el consentimiento o base de licitud adecuada para compartir dichos datos en la Plataforma.</p>

                    <p><strong>8. RESPONSABILIDAD E INDEMNIDAD</strong></p>
                    <p>El Cliente reconoce y acepta que es el único y exclusivo responsable por la seguridad, integridad física y salud de los usuarios finales que ingresen a sus instalaciones, ya sean sus propios clientes o usuarios provenientes de la red de la Plataforma.</p>
                    <p>En consecuencia, el Cliente se obliga a mantener indemne a Gym Bros, sus directores, representantes y empleados, respecto de cualquier demanda, querella, reclamo, acción indemnizatoria, multa administrativa o sanción de cualquier naturaleza.</p>

                    <p><strong>9. LIMITACIÓN DE RESPONSABILIDAD</strong></p>
                    <p>En ningún caso Gym Bros será responsable frente al Cliente por lucro cesante, pérdida de ingresos, pérdida de datos, pérdida de clientela o cualquier daño indirecto, imprevisto o consecuencial derivado del uso o la imposibilidad de uso de la Plataforma.</p>

                    <p><strong>10. PLAZO Y TERMINACIÓN</strong></p>
                    <p>La relación comercial tendrá una duración indefinida. Cualquiera de las partes podrá ponerle término mediante aviso previo de treinta (30) días corridos.</p>
                    <p>Sin perjuicio de lo anterior, Gym Bros podrá poner término inmediato en caso de incumplimiento de las causales establecidas en estos T&C.</p>

                    <p><strong>11. MODIFICACIÓN DE LOS TÉRMINOS Y CONDICIONES</strong></p>
                    <p>Gym Bros se reserva el derecho de modificar estos T&C en cualquier momento. Gym Bros notificará al Cliente sobre cualquier modificación sustancial mediante correo electrónico con al menos treinta (30) días corridos de anticipación.</p>

                    <p><strong>12. DISPOSICIONES GENERALES</strong></p>
                    <p>Cedibilidad: El Cliente no podrá ceder su cuenta ni los derechos emanados de estos T&C sin autorización previa de Gym Bros.</p>
                    <p>Confidencialidad: Las partes mantendrán en estricta reserva toda la información comercial y técnica a la que tengan acceso.</p>
                    <p>Propiedad Intelectual: La Plataforma y todo su material asociado son propiedad exclusiva de Gym Bros.</p>
                    <p>Domicilio y Jurisdicción: Para todos los efectos legales, las partes fijan su domicilio en la comuna de Santiago y se someten a la competencia de sus Tribunales Ordinarios de Justicia.</p>
                  </div>

                  <div className={`space-y-4 transition-opacity duration-300 ${hasScrolledToBottom ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                    {!hasScrolledToBottom && (
                      <p className="text-[10px] text-brand/80 animate-pulse text-center font-bold uppercase tracking-widest">Desliza hasta el final para aceptar</p>
                    )}
                    <label className="flex items-start gap-4 cursor-pointer group">
                      <div className={`mt-1 w-5 h-5 rounded border flex items-center justify-center transition-all ${formData.confirmPlan ? 'bg-brand border-brand' : 'border-white/20 group-hover:border-white/40'}`}>
                        <input 
                          type="checkbox" 
                          className="hidden" 
                          checked={formData.confirmPlan}
                          onChange={e => setFormData({...formData, confirmPlan: e.target.checked})}
                        />
                        {formData.confirmPlan && <Check size={14} className="text-black" />}
                      </div>
                      <span className="text-xs text-white/60 leading-relaxed">Confirmo que el <strong>{formData.plan}</strong> es el que deseo contratar.</span>
                    </label>

                    <label className="flex items-start gap-4 cursor-pointer group">
                      <div className={`mt-1 w-5 h-5 rounded border flex items-center justify-center transition-all ${formData.acceptQuality ? 'bg-brand border-brand' : 'border-white/20 group-hover:border-white/40'}`}>
                        <input 
                          type="checkbox" 
                          className="hidden" 
                          checked={formData.acceptQuality}
                          onChange={e => setFormData({...formData, acceptQuality: e.target.checked})}
                        />
                        {formData.acceptQuality && <Check size={14} className="text-black" />}
                      </div>
                      <span className="text-xs text-white/60 leading-relaxed">Acepto mantener los estándares de <strong>Calidad Elite</strong> en mis instalaciones.</span>
                    </label>

                    <label className="flex items-start gap-4 cursor-pointer group">
                      <div className={`mt-1 w-5 h-5 rounded border flex items-center justify-center transition-all ${formData.acceptValidation ? 'bg-brand border-brand' : 'border-white/20 group-hover:border-white/40'}`}>
                        <input 
                          type="checkbox" 
                          className="hidden" 
                          checked={formData.acceptValidation}
                          onChange={e => setFormData({...formData, acceptValidation: e.target.checked})}
                        />
                        {formData.acceptValidation && <Check size={14} className="text-black" />}
                      </div>
                      <span className="text-xs text-white/60 leading-relaxed">Me comprometo a la <strong>Validación Obligatoria</strong> de cada socio mediante QR.</span>
                    </label>

                    <label className="flex items-start gap-4 cursor-pointer group">
                      <div className={`mt-1 w-5 h-5 rounded border flex items-center justify-center transition-all ${formData.acceptTerms ? 'bg-brand border-brand' : 'border-white/20 group-hover:border-white/40'}`}>
                        <input 
                          type="checkbox" 
                          className="hidden" 
                          checked={formData.acceptTerms}
                          onChange={e => setFormData({...formData, acceptTerms: e.target.checked})}
                        />
                        {formData.acceptTerms && <Check size={14} className="text-black" />}
                      </div>
                      <span className="text-xs text-white/60 leading-relaxed">He leído y acepto los <strong>Términos y Condiciones</strong> de GymBrotherhood.</span>
                    </label>

                    <label className="flex items-start gap-4 cursor-pointer group">
                      <div className={`mt-1 w-5 h-5 rounded border flex items-center justify-center transition-all ${formData.hasAuthority ? 'bg-brand border-brand' : 'border-white/20 group-hover:border-white/40'}`}>
                        <input 
                          type="checkbox" 
                          className="hidden" 
                          checked={formData.hasAuthority}
                          onChange={e => setFormData({...formData, hasAuthority: e.target.checked})}
                        />
                        {formData.hasAuthority && <Check size={14} className="text-black" />}
                      </div>
                      <span className="text-xs text-white/60 leading-relaxed">Declaro que tengo la <strong>autoridad legal</strong> para tomar esta decisión en representación del gimnasio.</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button 
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 py-4 text-[10px] font-display uppercase tracking-widest text-white/40 hover:text-white transition-colors"
                  >
                    Atrás
                  </button>
                  <button 
                    type="submit"
                    disabled={loading || !formData.acceptTerms || !formData.acceptQuality || !formData.acceptValidation || !formData.hasAuthority || !formData.confirmPlan}
                    className="flex-[2] brutal-btn flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : 'Finalizar Solicitud'}
                    {!loading && <Check size={18} />}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default GymOnboarding;
